// src/utils/countryCodeMapper.js
// Utility to map country names or codes from various formats to ISO 3166-1 alpha-2 codes
// used by react-phone-number-input

/**
 * Maps country identifiers from the passport selection slide to phone input country codes.
 * Handles both button IDs (e.g., 'us', 'uk') and full country names from countries.json
 * 
 * @param {string} countryIdentifier - Can be a button ID, ISO alpha-3 code, or full country name
 * @returns {string|null} - ISO alpha-2 code (e.g., 'US') or null if no match found
 */
export function mapToPhoneCountryCode(countryIdentifier) {
  if (!countryIdentifier || typeof countryIdentifier !== 'string') {
    return null;
  }

  const identifier = countryIdentifier.trim();

  // Direct button ID to ISO alpha-2 mapping (from step 7 button options)
  const buttonIdMap = {
    'us': 'US',
    'uk': 'GB',
    'ca': 'CA',
    'au': 'AU',
    'de': 'DE',
    'fr': 'FR',
    'fi': 'FI',
    'se': 'SE',
    'dk': 'DK',
    'es': 'ES',
    'it': 'IT',
  };

  // Check if it's a button ID (lowercase, 2-3 chars)
  const lowerIdentifier = identifier.toLowerCase();
  if (buttonIdMap[lowerIdentifier]) {
    return buttonIdMap[lowerIdentifier];
  }

  // ISO alpha-3 to alpha-2 mapping for countries.json codes
  // Comprehensive mapping covering all countries in countries.json
  const alpha3ToAlpha2 = {
    'AFG': 'AF', 'ALA': 'AX', 'ALB': 'AL', 'DZA': 'DZ', 'ASM': 'AS',
    'AND': 'AD', 'AGO': 'AO', 'AIA': 'AI', 'ATA': 'AQ', 'ATG': 'AG',
    'ARG': 'AR', 'ARM': 'AM', 'ABW': 'AW', 'AUS': 'AU', 'AUT': 'AT',
    'AZE': 'AZ', 'BHS': 'BS', 'BHR': 'BH', 'BGD': 'BD', 'BRB': 'BB',
    'BLR': 'BY', 'BEL': 'BE', 'BLZ': 'BZ', 'BEN': 'BJ', 'BMU': 'BM',
    'BTN': 'BT', 'BOL': 'BO', 'BES': 'BQ', 'BIH': 'BA', 'BWA': 'BW',
    'BVT': 'BV', 'BRA': 'BR', 'IOT': 'IO', 'BRN': 'BN', 'BGR': 'BG',
    'BFA': 'BF', 'BDI': 'BI', 'KHM': 'KH', 'CMR': 'CM', 'CAN': 'CA',
    'CPV': 'CV', 'CYM': 'KY', 'CAF': 'CF', 'TCD': 'TD', 'CHL': 'CL',
    'CHN': 'CN', 'CXR': 'CX', 'CCK': 'CC', 'COL': 'CO', 'COM': 'KM',
    'COG': 'CG', 'COD': 'CD', 'COK': 'CK', 'CRI': 'CR', 'CIV': 'CI',
    'HRV': 'HR', 'CUB': 'CU', 'CUW': 'CW', 'CYP': 'CY', 'CZE': 'CZ',
    'DNK': 'DK', 'DJI': 'DJ', 'DMA': 'DM', 'DOM': 'DO', 'ECU': 'EC',
    'EGY': 'EG', 'SLV': 'SV', 'GNQ': 'GQ', 'ERI': 'ER', 'EST': 'EE',
    'ETH': 'ET', 'FLK': 'FK', 'FRO': 'FO', 'FJI': 'FJ', 'FIN': 'FI',
    'FRA': 'FR', 'GUF': 'GF', 'PYF': 'PF', 'ATF': 'TF', 'GAB': 'GA',
    'GMB': 'GM', 'GEO': 'GE', 'DEU': 'DE', 'GHA': 'GH', 'GIB': 'GI',
    'GRC': 'GR', 'GRL': 'GL', 'GRD': 'GD', 'GLP': 'GP', 'GUM': 'GU',
    'GTM': 'GT', 'GGY': 'GG', 'GIN': 'GN', 'GNB': 'GW', 'GUY': 'GY',
    'HTI': 'HT', 'HMD': 'HM', 'VAT': 'VA', 'HND': 'HN', 'HKG': 'HK',
    'HUN': 'HU', 'ISL': 'IS', 'IND': 'IN', 'IDN': 'ID', 'IRN': 'IR',
    'IRQ': 'IQ', 'IRL': 'IE', 'IMN': 'IM', 'ISR': 'IL', 'ITA': 'IT',
    'JAM': 'JM', 'JPN': 'JP', 'JEY': 'JE', 'JOR': 'JO', 'KAZ': 'KZ',
    'KEN': 'KE', 'KIR': 'KI', 'PRK': 'KP', 'KOR': 'KR', 'KWT': 'KW',
    'KGZ': 'KG', 'LAO': 'LA', 'LVA': 'LV', 'LBN': 'LB', 'LSO': 'LS',
    'LBR': 'LR', 'LBY': 'LY', 'LIE': 'LI', 'LTU': 'LT', 'LUX': 'LU',
    'MAC': 'MO', 'MKD': 'MK', 'MDG': 'MG', 'MWI': 'MW', 'MYS': 'MY',
    'MDV': 'MV', 'MLI': 'ML', 'MLT': 'MT', 'MHL': 'MH', 'MTQ': 'MQ',
    'MRT': 'MR', 'MUS': 'MU', 'MYT': 'YT', 'MEX': 'MX', 'FSM': 'FM',
    'MDA': 'MD', 'MCO': 'MC', 'MNG': 'MN', 'MNE': 'ME', 'MSR': 'MS',
    'MAR': 'MA', 'MOZ': 'MZ', 'MMR': 'MM', 'NAM': 'NA', 'NRU': 'NR',
    'NPL': 'NP', 'NLD': 'NL', 'NCL': 'NC', 'NZL': 'NZ', 'NIC': 'NI',
    'NER': 'NE', 'NGA': 'NG', 'NIU': 'NU', 'NFK': 'NF', 'MNP': 'MP',
    'NOR': 'NO', 'OMN': 'OM', 'PAK': 'PK', 'PLW': 'PW', 'PSE': 'PS',
    'PAN': 'PA', 'PNG': 'PG', 'PRY': 'PY', 'PER': 'PE', 'PHL': 'PH',
    'PCN': 'PN', 'POL': 'PL', 'PRT': 'PT', 'PRI': 'PR', 'QAT': 'QA',
    'REU': 'RE', 'ROU': 'RO', 'RUS': 'RU', 'RWA': 'RW', 'BLM': 'BL',
    'SHN': 'SH', 'KNA': 'KN', 'LCA': 'LC', 'MAF': 'MF', 'SPM': 'PM',
    'VCT': 'VC', 'WSM': 'WS', 'SMR': 'SM', 'STP': 'ST', 'SAU': 'SA',
    'SEN': 'SN', 'SRB': 'RS', 'SYC': 'SC', 'SLE': 'SL', 'SGP': 'SG',
    'SXM': 'SX', 'SVK': 'SK', 'SVN': 'SI', 'SLB': 'SB', 'SOM': 'SO',
    'ZAF': 'ZA', 'SGS': 'GS', 'SSD': 'SS', 'ESP': 'ES', 'LKA': 'LK',
    'SDN': 'SD', 'SUR': 'SR', 'SJM': 'SJ', 'SWZ': 'SZ', 'SWE': 'SE',
    'CHE': 'CH', 'SYR': 'SY', 'TWN': 'TW', 'TJK': 'TJ', 'TZA': 'TZ',
    'THA': 'TH', 'TLS': 'TL', 'TGO': 'TG', 'TKL': 'TK', 'TON': 'TO',
    'TTO': 'TT', 'TUN': 'TN', 'TUR': 'TR', 'TKM': 'TM', 'TCA': 'TC',
    'TUV': 'TV', 'UGA': 'UG', 'UKR': 'UA', 'ARE': 'AE', 'GBR': 'GB',
    'USA': 'US', 'UMI': 'UM', 'URY': 'UY', 'UZB': 'UZ', 'VUT': 'VU',
    'VEN': 'VE', 'VNM': 'VN', 'VGB': 'VG', 'VIR': 'VI', 'WLF': 'WF',
    'ESH': 'EH', 'YEM': 'YE', 'ZMB': 'ZM', 'ZWE': 'ZW'
  };

  // Check if it's an alpha-3 code
  const upperIdentifier = identifier.toUpperCase();
  if (alpha3ToAlpha2[upperIdentifier]) {
    return alpha3ToAlpha2[upperIdentifier];
  }

  // If not a code, try fuzzy matching on country name
  return fuzzyMatchCountryName(identifier);
}

/**
 * Performs fuzzy matching on country names to find the best ISO alpha-2 code match
 * Uses normalized comparison to handle variations in country names
 * 
 * @param {string} countryName - Full country name to match
 * @returns {string|null} - ISO alpha-2 code or null if no good match found
 */
function fuzzyMatchCountryName(countryName) {
  if (!countryName) return null;

  // Normalize the input: lowercase, remove common artifacts, extra spaces
  const normalize = (str) => {
    return str
      .toLowerCase()
      .replace(/[,.']/g, '') // Remove punctuation
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
  };

  const normalizedInput = normalize(countryName);

  // Country name to ISO alpha-2 mapping
  // Includes variations and common names from countries.json
  const nameMap = {
    'afghanistan': 'AF',
    'aland islands': 'AX',
    'åland islands': 'AX',
    'albania': 'AL',
    'algeria': 'DZ',
    'american samoa': 'AS',
    'andorra': 'AD',
    'angola': 'AO',
    'anguilla': 'AI',
    'antarctica': 'AQ',
    'antigua and barbuda': 'AG',
    'antigua & barbuda': 'AG',
    'argentina': 'AR',
    'armenia': 'AM',
    'aruba': 'AW',
    'australia': 'AU',
    'austria': 'AT',
    'azerbaijan': 'AZ',
    'bahamas': 'BS',
    'the bahamas': 'BS',
    'bahrain': 'BH',
    'bangladesh': 'BD',
    'barbados': 'BB',
    'belarus': 'BY',
    'belgium': 'BE',
    'belize': 'BZ',
    'benin': 'BJ',
    'bermuda': 'BM',
    'bhutan': 'BT',
    'bolivia': 'BO',
    'bolivia plurinational state of': 'BO',
    'bonaire sint eustatius and saba': 'BQ',
    'bosnia and herzegovina': 'BA',
    'bosnia & herzegovina': 'BA',
    'bosnia': 'BA',
    'botswana': 'BW',
    'bouvet island': 'BV',
    'brazil': 'BR',
    'british indian ocean territory': 'IO',
    'brunei': 'BN',
    'brunei darussalam': 'BN',
    'bulgaria': 'BG',
    'burkina faso': 'BF',
    'burundi': 'BI',
    'cambodia': 'KH',
    'cameroon': 'CM',
    'canada': 'CA',
    'cape verde': 'CV',
    'cayman islands': 'KY',
    'central african republic': 'CF',
    'chad': 'TD',
    'chile': 'CL',
    'china': 'CN',
    'christmas island': 'CX',
    'cocos keeling islands': 'CC',
    'cocos (keeling) islands': 'CC',
    'colombia': 'CO',
    'comoros': 'KM',
    'congo': 'CG',
    'congo the democratic republic of the': 'CD',
    'democratic republic of the congo': 'CD',
    'dr congo': 'CD',
    'cook islands': 'CK',
    'costa rica': 'CR',
    'cote divoire': 'CI',
    'côte divoire': 'CI',
    'ivory coast': 'CI',
    'croatia': 'HR',
    'cuba': 'CU',
    'curacao': 'CW',
    'curaçao': 'CW',
    'cyprus': 'CY',
    'czech republic': 'CZ',
    'czechia': 'CZ',
    'denmark': 'DK',
    'djibouti': 'DJ',
    'dominica': 'DM',
    'dominican republic': 'DO',
    'ecuador': 'EC',
    'egypt': 'EG',
    'el salvador': 'SV',
    'equatorial guinea': 'GQ',
    'eritrea': 'ER',
    'estonia': 'EE',
    'ethiopia': 'ET',
    'falkland islands': 'FK',
    'falkland islands (malvinas)': 'FK',
    'faroe islands': 'FO',
    'fiji': 'FJ',
    'finland': 'FI',
    'france': 'FR',
    'french guiana': 'GF',
    'french polynesia': 'PF',
    'french southern territories': 'TF',
    'gabon': 'GA',
    'gambia': 'GM',
    'the gambia': 'GM',
    'georgia': 'GE',
    'germany': 'DE',
    'ghana': 'GH',
    'gibraltar': 'GI',
    'greece': 'GR',
    'greenland': 'GL',
    'grenada': 'GD',
    'guadeloupe': 'GP',
    'guam': 'GU',
    'guatemala': 'GT',
    'guernsey': 'GG',
    'guinea': 'GN',
    'guinea-bissau': 'GW',
    'guyana': 'GY',
    'haiti': 'HT',
    'heard island and mcdonald islands': 'HM',
    'holy see': 'VA',
    'holy see (vatican city state)': 'VA',
    'vatican': 'VA',
    'vatican city': 'VA',
    'honduras': 'HN',
    'hong kong': 'HK',
    'hungary': 'HU',
    'iceland': 'IS',
    'india': 'IN',
    'indonesia': 'ID',
    'iran': 'IR',
    'iran islamic republic of': 'IR',
    'iraq': 'IQ',
    'ireland': 'IE',
    'isle of man': 'IM',
    'israel': 'IL',
    'italy': 'IT',
    'jamaica': 'JM',
    'japan': 'JP',
    'jersey': 'JE',
    'jordan': 'JO',
    'kazakhstan': 'KZ',
    'kenya': 'KE',
    'kiribati': 'KI',
    'north korea': 'KP',
    'korea democratic peoples republic of': 'KP',
    'south korea': 'KR',
    'korea republic of': 'KR',
    'kuwait': 'KW',
    'kyrgyzstan': 'KG',
    'laos': 'LA',
    'lao peoples democratic republic': 'LA',
    'latvia': 'LV',
    'lebanon': 'LB',
    'lesotho': 'LS',
    'liberia': 'LR',
    'libya': 'LY',
    'liechtenstein': 'LI',
    'lithuania': 'LT',
    'luxembourg': 'LU',
    'macao': 'MO',
    'macau': 'MO',
    'macedonia': 'MK',
    'macedonia the former yugoslav republic of': 'MK',
    'north macedonia': 'MK',
    'madagascar': 'MG',
    'malawi': 'MW',
    'malaysia': 'MY',
    'maldives': 'MV',
    'mali': 'ML',
    'malta': 'MT',
    'marshall islands': 'MH',
    'martinique': 'MQ',
    'mauritania': 'MR',
    'mauritius': 'MU',
    'mayotte': 'YT',
    'mexico': 'MX',
    'micronesia': 'FM',
    'micronesia federated states of': 'FM',
    'moldova': 'MD',
    'moldova republic of': 'MD',
    'monaco': 'MC',
    'mongolia': 'MN',
    'montenegro': 'ME',
    'montserrat': 'MS',
    'morocco': 'MA',
    'mozambique': 'MZ',
    'myanmar': 'MM',
    'burma': 'MM',
    'namibia': 'NA',
    'nauru': 'NR',
    'nepal': 'NP',
    'netherlands': 'NL',
    'new caledonia': 'NC',
    'new zealand': 'NZ',
    'nicaragua': 'NI',
    'niger': 'NE',
    'nigeria': 'NG',
    'niue': 'NU',
    'norfolk island': 'NF',
    'northern mariana islands': 'MP',
    'norway': 'NO',
    'oman': 'OM',
    'pakistan': 'PK',
    'palau': 'PW',
    'palestine': 'PS',
    'palestinian territory occupied': 'PS',
    'panama': 'PA',
    'papua new guinea': 'PG',
    'paraguay': 'PY',
    'peru': 'PE',
    'philippines': 'PH',
    'pitcairn': 'PN',
    'poland': 'PL',
    'portugal': 'PT',
    'puerto rico': 'PR',
    'qatar': 'QA',
    'reunion': 'RE',
    'réunion': 'RE',
    'romania': 'RO',
    'russia': 'RU',
    'russian federation': 'RU',
    'rwanda': 'RW',
    'saint barthelemy': 'BL',
    'saint barthélemy': 'BL',
    'saint helena ascension and tristan da cunha': 'SH',
    'saint helena': 'SH',
    'saint kitts and nevis': 'KN',
    'saint lucia': 'LC',
    'saint martin': 'MF',
    'saint martin (french part)': 'MF',
    'saint pierre and miquelon': 'PM',
    'saint vincent and the grenadines': 'VC',
    'samoa': 'WS',
    'san marino': 'SM',
    'sao tome and principe': 'ST',
    'saudi arabia': 'SA',
    'senegal': 'SN',
    'serbia': 'RS',
    'seychelles': 'SC',
    'sierra leone': 'SL',
    'singapore': 'SG',
    'sint maarten': 'SX',
    'sint maarten (dutch part)': 'SX',
    'slovakia': 'SK',
    'slovenia': 'SI',
    'solomon islands': 'SB',
    'somalia': 'SO',
    'south africa': 'ZA',
    'south georgia and the south sandwich islands': 'GS',
    'south sudan': 'SS',
    'spain': 'ES',
    'sri lanka': 'LK',
    'sudan': 'SD',
    'suriname': 'SR',
    'svalbard and jan mayen': 'SJ',
    'swaziland': 'SZ',
    'eswatini': 'SZ',
    'sweden': 'SE',
    'switzerland': 'CH',
    'syria': 'SY',
    'syrian arab republic': 'SY',
    'taiwan': 'TW',
    'taiwan province of china': 'TW',
    'tajikistan': 'TJ',
    'tanzania': 'TZ',
    'tanzania united republic of': 'TZ',
    'thailand': 'TH',
    'timor-leste': 'TL',
    'east timor': 'TL',
    'togo': 'TG',
    'tokelau': 'TK',
    'tonga': 'TO',
    'trinidad and tobago': 'TT',
    'trinidad & tobago': 'TT',
    'tunisia': 'TN',
    'turkey': 'TR',
    'turkmenistan': 'TM',
    'turks and caicos islands': 'TC',
    'tuvalu': 'TV',
    'uganda': 'UG',
    'ukraine': 'UA',
    'united arab emirates': 'AE',
    'uae': 'AE',
    'united kingdom': 'GB',
    'uk': 'GB',
    'great britain': 'GB',
    'united states': 'US',
    'usa': 'US',
    'united states of america': 'US',
    'us': 'US',
    'united states minor outlying islands': 'UM',
    'uruguay': 'UY',
    'uzbekistan': 'UZ',
    'vanuatu': 'VU',
    'venezuela': 'VE',
    'venezuela bolivarian republic of': 'VE',
    'vietnam': 'VN',
    'viet nam': 'VN',
    'virgin islands british': 'VG',
    'british virgin islands': 'VG',
    'virgin islands us': 'VI',
    'us virgin islands': 'VI',
    'wallis and futuna': 'WF',
    'western sahara': 'EH',
    'yemen': 'YE',
    'zambia': 'ZM',
    'zimbabwe': 'ZW'
  };

  // Direct match
  if (nameMap[normalizedInput]) {
    return nameMap[normalizedInput];
  }

  // Partial matching: try to find a country name that contains or is contained in the input
  // This handles cases like "USA" matching "United States" or vice versa
  for (const [name, code] of Object.entries(nameMap)) {
    // Skip very short entries to avoid false positives
    if (name.length < 4) continue;
    
    // Check if input contains the country name or country name contains input
    if (normalizedInput.includes(name) || name.includes(normalizedInput)) {
      // Additional validation: ensure it's a meaningful match
      // by checking that at least 50% of the shorter string matches
      const shorter = Math.min(name.length, normalizedInput.length);
      const longer = Math.max(name.length, normalizedInput.length);
      
      if (shorter / longer >= 0.5) {
        return code;
      }
    }
  }

  // No match found
  return null;
}
